import { Icon, Loading } from 'components';
import { Button } from 'modules';
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetTransactionDetailsQuery } from 'services';
import type { DetailData } from 'services/openapi';
import {
  formatDisplayAmount,
  formatDisplayDateTime,
  getTransactionStatusToneClass,
  isEmptyDisplayValue,
  navigateBackOrTo,
} from 'utils/helpers';

import { dataDefined } from './config';
import { MainRouteKeys } from 'routes/enums';

const PointTransactionDetailsFeature = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { transactionCode } = useParams();
  const returnPath = location.state?.pointTransactionsList;
  const listPath =
    typeof returnPath === 'string' &&
    (returnPath === MainRouteKeys.PointTransactions ||
      returnPath.startsWith(`${MainRouteKeys.PointTransactions}?`))
      ? returnPath
      : MainRouteKeys.PointTransactions;

  const { data, isLoading, isError } = useGetTransactionDetailsQuery(
    String(transactionCode || ''),
  );

  const detailsData = data?.data as DetailData | undefined;
  const transaction = detailsData?.transaction;
  const bidvErrorDesc = detailsData?.info?.bidvErrorDesc;
  const statusToneClass = getTransactionStatusToneClass(transaction?.status);

  const renderFieldRows = (section: (typeof dataDefined)[number]) => {
    const parentData = section.getData(detailsData);

    if (!parentData) return [];

    return section.value
      .map((field) => {
        const fieldValue = parentData?.[field.key];

        if (isEmptyDisplayValue(fieldValue)) return null;

        const className = field.className?.(fieldValue) || '';
        const displayValue = field.format
          ? field.format(fieldValue)
          : fieldValue;

        return (
          <div className='detail-row' key={field.key}>
            <span className='detail-label'>{field.label}</span>
            <span className={`detail-value ${className}`}>{displayValue}</span>
          </div>
        );
      })
      .filter(Boolean);
  };

  const renderHtml = () => {
    if (!detailsData) return null;

    return dataDefined.map((section) => {
      const rows = renderFieldRows(section);

      if (!rows.length) return null;

      return (
        <section key={section.key} className='detail-card'>
          <div className='detail-card__header'>
            <span className='detail-card__mark' />
            <h2>{section.label}</h2>
          </div>
          <div className='detail-card__body'>{rows}</div>
        </section>
      );
    });
  };

  return (
    <div className='point-transaction-detail'>
      <Loading isLoading={isLoading}>
        {isError ? (
          <div className='detail-empty textDanger'>
            Không thể tải chi tiết giao dịch
          </div>
        ) : (
          detailsData && (
            <>
              <div className='detail-toolbar'>
                <Button
                  label='Trở về danh sách'
                  onClick={() =>
                    navigateBackOrTo(navigate, listPath, {
                      preferFallback: true,
                    })
                  }
                  type='button'
                  buttonType='primary-outline'
                  className='detail-back-button'
                  icon={() => <Icon.ArrowLeft />}
                />
              </div>

              <section className='detail-hero'>
                <div className='detail-hero__content'>
                  <div className='detail-hero__eyebrow'>
                    <span className='detail-hero__icon'>
                      <Icon.PointTransaction />
                    </span>
                    Giao dịch điểm
                  </div>
                  <h1>{transaction?.transactionCode || transactionCode}</h1>
                  <div className='detail-hero__meta'>
                    <span>{transaction?.transactionType || '--'}</span>
                    <span>{formatDisplayDateTime(transaction?.payAt)}</span>
                  </div>
                </div>

                <div className='detail-hero__aside'>
                  <span className={`detail-status ${statusToneClass}`}>
                    {transaction?.status || '--'}
                  </span>
                  <div className='detail-amount'>
                    {formatDisplayAmount(transaction?.value)}
                  </div>
                  <span className='detail-amount__label'>
                    Số dư sau GD:{' '}
                    {formatDisplayAmount(transaction?.balanceAfter)}
                  </span>
                </div>
              </section>

              {bidvErrorDesc && (
                <section className='detail-alert'>
                  <span className='detail-alert__icon'>
                    <Icon.Warning />
                  </span>
                  <div>
                    <div className='detail-alert__title'>Lỗi:</div>
                    <div className='detail-alert__title'>{bidvErrorDesc}</div>
                  </div>
                </section>
              )}

              <div className='detail-layout'>
                <div className='detail-layout__main'>{renderHtml()}</div>
              </div>
            </>
          )
        )}
      </Loading>
    </div>
  );
};

export default PointTransactionDetailsFeature;
